import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

/** Public page hit by clicking the link in the password-reset email.
 *  Reads ?token=..., pre-validates it against the backend so an expired or
 *  already-used link shows an error immediately instead of rendering the
 *  form. Only if the token is still valid do we show "set new password". */
export default function ResetPasswordPage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token') || '';
  const [pw, setPw]         = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(false);
  // 'checking' until the backend confirms the token is still usable; 'valid'
  // shows the form; 'invalid' shows the inline error card (with linkError set).
  const [linkState, setLinkState] = useState(token ? 'checking' : 'invalid');
  const [linkError, setLinkError] = useState(token ? '' : 'Missing reset token in the URL.');

  useEffect(() => {
    if (!token) return;
    authAPI.validateResetToken(token)
      .then(() => setLinkState('valid'))
      .catch(err => {
        setLinkError(err.response?.data?.error || 'This reset link is no longer valid.');
        setLinkState('invalid');
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 6)      return setError('Password must be at least 6 characters');
    if (pw !== confirm)     return setError('Passwords do not match');
    if (!token)             return setError('Missing reset token in the URL.');
    setBusy(true);
    try {
      await authAPI.resetPassword(token, pw);
      setDone(true);
      // Auto-redirect to sign-in after 2s
      setTimeout(() => navigate('/?auth=signin', { replace: true }), 2200);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset password. The link may be expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <Link to="/" style={brand}>
          <span style={brandMark}>🎬</span>
          <span style={brandName}>CreatorKhata</span>
        </Link>

        {done ? (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
            <h1 style={title}>Password updated</h1>
            <p style={sub}>Redirecting you to sign in…</p>
          </>
        ) : linkState === 'checking' ? (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⏳</div>
            <h1 style={title}>Checking your link…</h1>
            <p style={sub}>Just a moment.</p>
          </>
        ) : linkState === 'invalid' ? (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚠️</div>
            <h1 style={title}>Link unavailable</h1>
            <p style={sub}>{linkError}</p>
            <Link to="/?auth=forgot" style={ctaPrimary}>Request a new link →</Link>
          </>
        ) : (
          <>
            <h1 style={title}>Set a new password</h1>
            <p style={sub}>Pick something at least 6 characters long.</p>
            <form onSubmit={submit}>
              <input
                type="password" placeholder="New password"
                value={pw} onChange={e => setPw(e.target.value)}
                required autoFocus
                style={input}
              />
              <input
                type="password" placeholder="Confirm new password"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                required
                style={input}
              />
              {error && (
                <div style={errBox}>⚠️ {error}</div>
              )}
              <button type="submit" disabled={busy} style={ctaPrimary}>
                {busy ? 'Saving…' : 'Set new password →'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', background: '#fafafa',
  fontFamily: "'Inter', sans-serif",
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1.5rem',
};
const cardStyle = {
  background: 'white', border: '1px solid #e4e4e7',
  borderRadius: 20, padding: '2.5rem 2rem',
  maxWidth: 440, width: '100%', textAlign: 'center',
  boxShadow: '0 10px 30px rgba(10,10,12,0.06)',
};
const brand = {
  display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
  textDecoration: 'none', color: '#0a0a0c', marginBottom: '1.5rem',
};
const brandMark = {
  width: 32, height: 32, borderRadius: 9,
  background: 'linear-gradient(135deg,#6366f1,#ec4899)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '0.85rem',
};
const brandName = {
  fontFamily: "'Syne', sans-serif", fontWeight: 800,
  fontSize: '1.05rem', letterSpacing: '-0.03em',
};
const title = {
  fontFamily: "'Syne', sans-serif",
  fontWeight: 800, fontSize: '1.55rem',
  letterSpacing: '-0.02em', color: '#0a0a0c',
  margin: '0 0 0.5rem',
};
const sub = { color: '#52525b', fontSize: '0.95rem', lineHeight: 1.55, margin: '0 0 1.5rem' };
const input = {
  width: '100%', padding: '0.85rem 1rem',
  border: '1.5px solid #e4e4e7', borderRadius: 11,
  fontSize: '0.95rem', fontFamily: "'Inter', sans-serif",
  outline: 'none', marginBottom: '0.65rem',
};
const errBox = {
  padding: '0.7rem 0.9rem',
  background: '#fef2f2', border: '1px solid #fecaca',
  borderRadius: 10, fontSize: '0.85rem', color: '#b91c1c',
  marginBottom: '0.65rem',
};
const ctaPrimary = {
  width: '100%', cursor: 'pointer', border: 'none',
  background: '#0a0a0c', color: 'white',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700, fontSize: '0.95rem',
  padding: '0.95rem', borderRadius: 12, marginTop: '0.25rem',
};
